/**
 * ProjectSelector — Relay's single branded project selector
 * (track:project-selector-standard). Every place a person picks a project
 * renders this component; plain `<select>` project pickers are retired
 * (ProjectSelector.test.tsx guards that).
 *
 * Originally the Control Center's top-left dropdown (CC v2 parity with v1's
 * `.cc-project-selector`). Reads the project list from the shared
 * RelayDataProvider workspace doc (`workspace.projects`) or a `projects` prop. The value is
 * controlled by project slug (`slugify(name)`); callers own their state.
 * The Control Center wires it to useActiveProject, which persists the
 * `active_project` context key via the real set_context/get_context path
 * (see that hook) — deliberately NOT the doc's `active_project_slug` field
 * or local state, which shipped the "resets to All on sprint lock" bug
 * (seq #24790). That wiring stays in the Control Center, not here.
 *
 * `mode` picks the null behaviour:
 *   "all"      — "All Projects" option; null means all (default, the CC).
 *   "optional" — a "No project" option (label via `noneLabel`); null means none.
 *   "required" — no null option; null shows a "Pick a project" placeholder.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useOptionalRelayData } from "../lib/RelayDataProvider";
import { slugify, type ProjectRecord } from "../lib/projectsMigration";

export type ProjectSelectorMode = "all" | "optional" | "required";

const NO_PROJECT_COLOR = "#9AA0AB";

/** Trigger look, shared with the read-only badge so the two never drift. */
export const PROJECT_TRIGGER_CLASS =
  "flex items-center gap-1.5 text-[13px] font-medium text-[var(--muted)]";

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

/**
 * `projects` wins when given (pages outside the Control Center already hold
 * the workspace's list); otherwise read it from RelayDataProvider.
 */
function useProjectOptions(projectsProp?: ProjectRecord[]): ProjectOption[] {
  const ctx = useOptionalRelayData();
  const fromCtx = ctx && Array.isArray(ctx.workspace.projects) ? (ctx.workspace.projects as ProjectRecord[]) : [];
  const raw = projectsProp ?? fromCtx;
  return raw.map(r => ({ id: slugify(r.name), name: r.name, color: r.color }));
}

/**
 * Read-only indicator in the selector's trigger style — for surfaces that
 * mirror a project chosen elsewhere (Sprint Board header) and must not be a picker.
 */
export function ProjectBadge({ name }: { name: string | null }) {
  const projects = useProjectOptions();
  const match = name ? projects.find(p => p.name === name || p.id === name) : null;
  return (
    <span className={PROJECT_TRIGGER_CLASS} data-testid="project-badge">
      <span
        className="h-2 w-2 flex-shrink-0 rounded-full"
        style={{ background: match?.color ?? NO_PROJECT_COLOR }}
      />
      {name ?? "All"}
    </span>
  );
}

export function ProjectSelector({
  value,
  onChange,
  mode = "all",
  noneLabel = "No project",
  openRequest = 0,
  projects: projectsProp,
}: {
  /** Selected project slug, or null. */
  value: string | null;
  onChange: (slug: string | null) => void;
  mode?: ProjectSelectorMode;
  /** Label of the null option in "optional" mode. */
  noneLabel?: string;
  /** Increment to open the dropdown programmatically (e.g. "Pick a project to clean up"). */
  openRequest?: number;
  /** The workspace's projects. Omit inside the Control Center, where the shared data provider has them. */
  projects?: ProjectRecord[];
}) {
  const projects = useProjectOptions(projectsProp);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (openRequest > 0) setOpen(true);
  }, [openRequest]);

  // Move focus into the list when it opens, onto the selected option if any.
  useEffect(() => {
    if (!open) return;
    const opts = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    (opts.find(o => o.getAttribute("aria-selected") === "true") ?? opts[0])?.focus();
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const handleSelect = (slug: string | null) => {
    onChange(slug);
    close(true);
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const opts = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    if (opts.length === 0) return;
    const i = opts.indexOf(document.activeElement as HTMLElement);
    const next = e.key === "ArrowDown" ? (i + 1) % opts.length : (i <= 0 ? opts.length - 1 : i - 1);
    opts[next].focus();
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape" && open) {
      e.stopPropagation();
      setOpen(false);
    }
  };

  const selected = projects.find(p => p.id === value) ?? null;
  const nullLabel = mode === "all" ? "All Projects" : noneLabel;
  const triggerLabel = selected?.name ?? (mode === "required" ? "Pick a project" : nullLabel);

  const optionClass = (isSelected: boolean) =>
    `flex w-full cursor-pointer items-center gap-2 rounded-md border-none px-3 py-2 text-left text-[13px] ${
      isSelected
        ? "bg-[rgba(95,196,192,.08)] font-semibold text-[var(--teal-dark)]"
        : "bg-transparent text-[var(--ink)] hover:bg-[rgba(0,0,0,0.04)]"
    }`;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-label="Project"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${PROJECT_TRIGGER_CLASS} cursor-pointer border-none bg-transparent p-0 hover:text-[var(--teal-dark)]`}
      >
        <span
          className="h-2 w-2 flex-shrink-0 rounded-full"
          style={{ background: selected?.color ?? NO_PROJECT_COLOR }}
        />
        {triggerLabel}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          className={`flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        >
          <path
            d="M3 2 L7 5 L3 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label="Project"
          onKeyDown={onListKeyDown}
          // Border/radius/shadow match FloatingChat's roster picker
          // (FloatingChat.tsx:1159-1384) — the closest brand-standard
          // analog to this dropdown: 2px teal border (panel-level chrome
          // tier), 12px radius, teal-tinted shadow in place of plain
          // black-alpha.
          className="absolute left-0 top-full z-10 mt-2 min-w-[180px] rounded-xl border-2 border-[var(--teal-mid)] bg-[var(--paper-2)] p-1 shadow-[0_8px_24px_rgba(5,71,80,0.2)]"
        >
          {mode !== "required" && (
            <button
              type="button"
              role="option"
              aria-selected={!selected}
              onClick={() => handleSelect(null)}
              className={optionClass(!selected)}
            >
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: NO_PROJECT_COLOR }} />
              {nullLabel}
            </button>
          )}
          {projects.map(p => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={p.id === selected?.id}
              onClick={() => handleSelect(p.id)}
              className={optionClass(p.id === selected?.id)}
            >
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: p.color }} />
              {p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
